import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Button,
  DataTable,
  DataTableRenderProps,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Dropdown,
  DatePicker,
  DatePickerInput,
  RadioButtonGroup,
  RadioButton,
  Pagination,
  Tile,
} from '@carbon/react';
import { Add, DashboardReference } from '@carbon/react/icons';
import { PageHeader } from 'components/page-header';
import { useAtom } from 'jotai';
import { createStructureGridSearchAtom } from './atoms';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { SkeletonTableRows } from 'components/skeleton-table-rows';
import { EmptyState } from 'components/empty-state';
import ValidityStatusIndicator from 'components/structure-grids/validity-status-indicator';
import style from './structure-grids-management.module.scss';
import { Helmet } from 'react-helmet-async';
import { pageTitle } from 'utils/page-title';
import getFilterOptions, {
  PROCESSING_OPTIONS,
  VALIDITY_OPTIONS,
} from './utils';
import { format } from 'date-fns';
import { capitalize } from 'lodash-es';
import { format0 } from 'utils/number';

interface TableRow {
  id: string;
  cells: Array<{ value: string }>;
}

const formattedToday = format(new Date(), 'MM/dd/yyyy');

const StructureGridsManagementPage = () => {
  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);

  const [dateFilterType, setDateFilterType] = useState<'none' | 'created'>(
    (params.get('date_filter_type') as 'none' | 'created') || 'none'
  );
  const [startDate, setStartDate] = useState(
    params.get('start_date') ? new Date(params.get('start_date')!) : undefined
  );
  const [endDate, setEndDate] = useState(
    params.get('end_date') ? new Date(params.get('end_date')!) : undefined
  );
  const [selectedBasin, setSelectedBasin] = useState<string>('All');
  const [selectedGeologicInterval, setSelectedGeologicInterval] =
    useState<string>('All');
  const [selectedDataOwner, setSelectedDataOwner] = useState<string>('All');
  const [selectedDataSource, setSelectedDataSource] = useState<string>('All');
  const [selectedValidity, setSelectedValidity] = useState<string>('All');
  const [selectedProcessedStatus, setSelectedProcessedStatus] =
    useState<string>('All');
  const pageSize = parseInt(params.get('page_size') || '100', 10);

  const [filterOptions, setFilterOptions] = useState<{
    basins: string[];
    geologicIntervals: string[];
    dataOwners: string[];
    dataSources: string[];
    validity: string[];
    status: string[];
  }>({
    basins: [],
    geologicIntervals: [],
    dataOwners: [],
    dataSources: [],
    validity: [],
    status: [],
  });
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  const [filterOptionsError, setFilterOptionsError] = useState<Error | null>(
    null
  );

  useEffect(() => {
    setFilterOptionsLoading(true);
    const params = new URLSearchParams(window.location.search);
    const basinName = params.get('basin_name');
    const geologicInterval = params.get('geologic_interval');
    const dataOwner = params.get('owner');
    const dataSource = params.get('source');
    const validity = params.get('validity');
    const status = params.get('status');

    getFilterOptions()
      .then((data) => {
        const withAllOption = {
          basins: ['All', ...data.basins],
          geologicIntervals: ['All', ...data.geologicIntervals],
          dataOwners: ['All', ...data.dataOwners],
          dataSources: ['All', ...data.dataSources],
          validity: VALIDITY_OPTIONS,
          status: PROCESSING_OPTIONS,
        };
        setFilterOptions(withAllOption);

        if (basinName && withAllOption.basins.includes(basinName)) {
          handleFilterSelection('basin_name', basinName, setSelectedBasin);
        }
        if (
          geologicInterval &&
          withAllOption.geologicIntervals.includes(geologicInterval)
        ) {
          handleFilterSelection(
            'geologic_interval',
            geologicInterval,
            setSelectedGeologicInterval
          );
        }
        if (dataOwner && withAllOption.dataOwners.includes(dataOwner)) {
          handleFilterSelection('owner', dataOwner, setSelectedDataOwner);
        }
        if (dataSource && withAllOption.dataSources.includes(dataSource)) {
          handleFilterSelection('source', dataSource, setSelectedDataSource);
        }
        if (validity && withAllOption.validity.includes(validity)) {
          handleFilterSelection('validity', validity, setSelectedValidity);
        }
        if (status && withAllOption.status.includes(status)) {
          handleFilterSelection('status', status, setSelectedProcessedStatus);
        }
      })
      .catch((error) => {
        console.error('Error fetching filter options', error);
        setFilterOptionsError(error);
      })
      .finally(() => {
        setFilterOptionsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create search atoms
  const searchAtoms = useMemo(
    () => createStructureGridSearchAtom(pageSize),
    [pageSize]
  );
  const [searchAtomValue, setSearchPayload] = useAtom(searchAtoms.searchAtom);

  // Handle filter changes
  const handleFilterChange = useCallback(
    (filterName: string, value: string) => {
      const params = new URLSearchParams(location.search);
      if (value && value !== 'All') {
        params.set(filterName, value);
      } else {
        params.delete(filterName);
      }
      params.set('page', '1');
      history.push({ search: params.toString() });

      setSearchPayload((current) => ({
        ...current,
        [filterName]: value && value !== 'All' ? value : undefined,
        page: 1,
      }));
    },
    [location.search, history, setSearchPayload]
  );

  // Handle date filter changes
  const handleDateFilterChange = useCallback(
    (type: string, start?: Date, end?: Date) => {
      const params = new URLSearchParams(location.search);
      const formattedStartDate = start
        ? `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(
            2,
            '0'
          )}-${String(start.getDate()).padStart(2, '0')}`
        : '';
      const formattedEndDate = end
        ? `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(
            2,
            '0'
          )}-${String(end.getDate()).padStart(2, '0')}`
        : '';
      params.set('date_filter_type', type);
      if (type !== 'none') {
        params.set('start_date', formattedStartDate);
        params.set('end_date', formattedEndDate);
      } else {
        params.delete('start_date');
        params.delete('end_date');
      }
      params.set('page', '1');
      history.push({ search: params.toString() });

      setSearchPayload((current) => ({
        ...current,
        date_filter_type: type as 'none' | 'created',
        created_at:
          type !== 'none'
            ? { start: formattedStartDate, end: formattedEndDate }
            : undefined,
        page: 1,
      }));
    },
    [location.search, history, setSearchPayload]
  );

  // Handle pagination
  const handlePageChange = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const params = new URLSearchParams(location.search);
    params.set('page', page.toString());
    params.set('page_size', pageSize.toString());
    history.push({ search: params.toString() });

    setSearchPayload((current) => ({
      ...current,
      page,
      pageSize,
    }));
  };

  const handleFilterSelection = useCallback(
    (
      filterName: string,
      selectedItem: string | null | undefined,
      setState: (value: string) => void
    ) => {
      if (selectedItem) {
        setState(selectedItem);
        handleFilterChange(filterName, selectedItem);
      } else {
        setState('All');
        handleFilterChange(filterName, 'All');
      }
    },
    [handleFilterChange]
  );

  const mappedResults = useMemo(() => {
    const results = searchAtomValue.data?.results ?? [];

    const formatResolution = (w: number | null, h: number | null) => {
      if (w === null || h === null || w < 1 || h < 1) return 'Unknown';
      return `${format0(Math.round(Number(w)))} ft x ${format0(
        Math.round(Number(h))
      )} ft`;
    };

    return results.map((result: any) => ({
      ...result,
      id: result.id.toString(),
      gridProcessed: result.gridProcessed ? 'Yes' : 'No',
      createdAt: result.createdAt
        ? new Date(result.createdAt).toLocaleDateString()
        : '',
      gridCellSize: formatResolution(
        result.cellWidthFeet,
        result.cellHeightFeet
      ),
    }));
  }, [searchAtomValue.data]);

  return (
    <>
      <Helmet>
        <title>{pageTitle('Structure Grid Management')}</title>
      </Helmet>
      <div className={style.structureGridsPageHeader}>
        <PageHeader
          title="Structure Grid Management"
          breadcrumbs={[['structure_grids', 'Structure Grid Management']]}
          actions={
            <div className={style.actionsArea}>
              <Button
                renderIcon={Add}
                onClick={() => history.push('/add_structure_grids')}
              >
                Add Structure Grid
              </Button>
              <Button
                renderIcon={DashboardReference}
                onClick={() => history.push('/structure_grid_collections')}
              >
                Manage Collections
              </Button>
            </div>
          }
        />
      </div>
      <div className={style.mainContent}>
        {filterOptionsError && (
          <div className={style.errorMessage}>
            <p>
              Error fetching filter options. Please reach out on
              #insights-oncall if this issue persists.
            </p>
          </div>
        )}
        <div className={style.filtersRow}>
          <div className={style.dateFilterContainer}>
            <div>
              <h4>Date Filter</h4>
              <div className={style.dateInputs}>
                <div className={style.datePickerContainer}>
                  <div className={style.dateInputWrapper}>
                    <DatePicker
                      value={startDate}
                      dateFormat="m/d/Y"
                      datePickerType="single"
                      minDate={format(new Date('1900-01-01'), 'MM/dd/yyyy')}
                      maxDate={formattedToday}
                      onChange={([date]) => {
                        setStartDate(date);
                        handleDateFilterChange(dateFilterType, date, endDate);
                      }}
                    >
                      <DatePickerInput
                        id="start-date-input"
                        placeholder="mm/dd/yyyy"
                        labelText=""
                        size="md"
                        disabled={dateFilterType === 'none'}
                        data-testid="start-date-input"
                      />
                    </DatePicker>
                  </div>
                </div>
                <div className={style.datePickerContainer}>
                  <div className={style.dateInputWrapper}>
                    <DatePicker
                      value={endDate}
                      dateFormat="m/d/Y"
                      datePickerType="single"
                      minDate={format(new Date('1900-01-01'), 'MM/dd/yyyy')}
                      maxDate={formattedToday}
                      onChange={([date]) => {
                        setEndDate(date);
                        handleDateFilterChange(dateFilterType, startDate, date);
                      }}
                    >
                      <DatePickerInput
                        id="end-date-input"
                        placeholder="mm/dd/yyyy"
                        labelText=""
                        size="md"
                        disabled={dateFilterType === 'none'}
                        data-testid="end-date-input"
                      />
                    </DatePicker>
                  </div>
                </div>
              </div>
            </div>
            <div className={style.radioGroup}>
              <RadioButtonGroup
                name="date-filter-type"
                orientation="vertical"
                valueSelected={dateFilterType}
                onChange={(value) => {
                  setDateFilterType(value as 'none' | 'created');
                  if (value === 'none') {
                    setStartDate(undefined);
                    setEndDate(undefined);
                  }
                  handleDateFilterChange(
                    value as 'none' | 'created',
                    value === 'none' ? undefined : startDate,
                    value === 'none' ? undefined : endDate
                  );
                }}
              >
                <RadioButton labelText="None" value="none" id="none" />
                <RadioButton
                  labelText="Created At"
                  value="created_at"
                  id="created_at"
                  data-testid="created-at-radio-button"
                />
              </RadioButtonGroup>
            </div>
          </div>

          <div className={style.filterContainer}>
            <h4>Basin</h4>
            <Dropdown
              id="basin-filter"
              data-testid="basin-filter"
              titleText=""
              label="Select"
              items={filterOptions.basins}
              itemToString={(item) => (item ? item : '')}
              onChange={({ selectedItem }) =>
                handleFilterSelection(
                  'basin_name',
                  selectedItem,
                  setSelectedBasin
                )
              }
              selectedItem={selectedBasin}
            />
          </div>

          <div className={style.filterContainer}>
            <h4>Geologic Interval</h4>
            <Dropdown
              id="geologic-interval-filter"
              titleText=""
              label="Select"
              items={filterOptions.geologicIntervals}
              itemToString={(item) => (item ? item : '')}
              onChange={({ selectedItem }) =>
                handleFilterSelection(
                  'geologic_interval',
                  selectedItem,
                  setSelectedGeologicInterval
                )
              }
              selectedItem={selectedGeologicInterval}
            />
          </div>

          <div className={style.filterContainer}>
            <h4>Data Owner</h4>
            <Dropdown
              id="data-owner-filter"
              titleText=""
              label="Select"
              items={filterOptions.dataOwners}
              itemToString={(item) => (item ? item : '')}
              onChange={({ selectedItem }) =>
                handleFilterSelection(
                  'owner',
                  selectedItem,
                  setSelectedDataOwner
                )
              }
              selectedItem={selectedDataOwner}
            />
          </div>

          <div className={style.filterContainer}>
            <h4>Data Source</h4>
            <Dropdown
              id="data-source-filter"
              titleText=""
              label="Select"
              items={filterOptions.dataSources}
              itemToString={(item) => (item ? item : '')}
              onChange={({ selectedItem }) =>
                handleFilterSelection(
                  'source',
                  selectedItem,
                  setSelectedDataSource
                )
              }
              selectedItem={selectedDataSource}
            />
          </div>
          <div className={style.filterContainer}>
            <h4>Validity</h4>
            <Dropdown
              id="validity-filter"
              data-testid="validity-filter"
              titleText=""
              label="Validity"
              items={VALIDITY_OPTIONS}
              itemToString={(item) => (item ? capitalize(item) : '')}
              onChange={({ selectedItem }) =>
                handleFilterSelection(
                  'validity',
                  selectedItem,
                  setSelectedValidity
                )
              }
              selectedItem={selectedValidity}
            />
          </div>
          <div className={style.filterContainer}>
            <h4>Processed</h4>
            <Dropdown
              id="processed-filter"
              data-testid="processed-filter"
              titleText=""
              label="Processed"
              items={PROCESSING_OPTIONS}
              itemToString={(item) => (item ? capitalize(item) : '')}
              onChange={({ selectedItem }) =>
                handleFilterSelection(
                  'status',
                  selectedItem,
                  setSelectedProcessedStatus
                )
              }
              selectedItem={selectedProcessedStatus}
            />
          </div>
        </div>
      </div>
      <div className={style.tableContainer}>
        {!searchAtomValue.data?.results.length ? (
          <Tile>
            <EmptyState
              headerText="No Structure Grids Available"
              helperText="Currently there are no structure grids found"
            />
          </Tile>
        ) : (
          <>
            <div>
              <Pagination
                backwardText="Previous page"
                forwardText="Next page"
                itemsPerPageText="Items per page:"
                page={searchAtomValue.currentPayload?.page || 1}
                pageSize={pageSize}
                pageSizes={[100]}
                totalItems={searchAtomValue?.data?.totalCount || 0}
                onChange={handlePageChange}
                size="md"
              />
            </div>
            <DataTable
              rows={mappedResults}
              headers={[
                { key: 'basinName', header: 'Basin' },
                { key: 'geologicInterval', header: 'Geologic Interval' },
                { key: 'gridSurface', header: 'Grid Surface' },
                { key: 'gridCellSize', header: 'Grid Cell Size (W x H)' },
                { key: 'owner', header: 'Data Owner' },
                { key: 'status', header: 'Processing status' },
                { key: 'source', header: 'Data Source' },
                { key: 'validity', header: 'Validity' },
                { key: 'createdAt', header: 'Created At' },
              ]}
            >
              {({
                rows,
                getTableProps,
                getHeaderProps,
                getRowProps,
              }: DataTableRenderProps<any, any[]>) => (
                <Table
                  {...getTableProps()}
                  className={style.structureGridsTable}
                >
                  <TableHead>
                    <TableRow>
                      <TableHeader
                        {...getHeaderProps({
                          header: { key: 'basinName', header: 'Basin' },
                        })}
                      >
                        Basin
                      </TableHeader>
                      <TableHeader
                        {...getHeaderProps({
                          header: {
                            key: 'geologicInterval',
                            header: 'Geologic Interval',
                          },
                        })}
                      >
                        Geologic Interval
                      </TableHeader>
                      <TableHeader
                        {...getHeaderProps({
                          header: {
                            key: 'gridSurface',
                            header: 'Grid Surface',
                          },
                        })}
                      >
                        Grid Surface
                      </TableHeader>
                      <TableHeader
                        {...getHeaderProps({
                          header: {
                            key: 'gridCellSize',
                            header: 'Grid Cell Size (W x H)',
                          },
                        })}
                      >
                        Grid Cell Size (W x H)
                      </TableHeader>
                      <TableHeader
                        {...getHeaderProps({
                          header: { key: 'owner', header: 'Data Owner' },
                        })}
                      >
                        Data Owner
                      </TableHeader>
                      <TableHeader
                        {...getHeaderProps({
                          header: {
                            key: 'status',
                            header: 'Processing status',
                          },
                        })}
                      >
                        Processing status
                      </TableHeader>

                      <TableHeader
                        {...getHeaderProps({
                          header: { key: 'source', header: 'Data Source' },
                        })}
                      >
                        Data Source
                      </TableHeader>
                      <TableHeader
                        {...getHeaderProps({
                          header: { key: 'validity', header: 'Validity' },
                        })}
                      >
                        Validity
                      </TableHeader>
                      <TableHeader
                        {...getHeaderProps({
                          header: { key: 'createdAt', header: 'Created At' },
                        })}
                      >
                        Created At
                      </TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchAtomValue.loading || filterOptionsLoading ? (
                      <SkeletonTableRows rows={5} columns={8} />
                    ) : (
                      rows.map((row: any) => (
                        // eslint-disable-next-line @eslint-react/no-missing-key
                        <TableRow {...getRowProps({ row })}>
                          {row.cells.map((cell: any, index: number) => (
                            <TableCell key={index}>
                              {cell.info.header === 'status' ? (
                                capitalize(String(cell.value))
                              ) : cell.info.header === 'geologicInterval' ? (
                                <Link
                                  to={`/structure_grids/${row.id}`}
                                  className={style.geologicIntervalLink}
                                >
                                  {String(cell.value)}
                                </Link>
                              ) : cell.info.header === 'validity' ? (
                                <ValidityStatusIndicator
                                  status={String(cell.value)}
                                />
                              ) : (
                                String(cell.value)
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              )}
            </DataTable>
          </>
        )}

        <div>
          <Pagination
            backwardText="Previous page"
            forwardText="Next page"
            itemsPerPageText="Items per page:"
            page={searchAtomValue.currentPayload?.page || 1}
            pageSize={pageSize}
            pageSizes={[100]}
            totalItems={searchAtomValue?.data?.totalCount || 0}
            onChange={handlePageChange}
            size="md"
          />
        </div>
      </div>
    </>
  );
};

export default StructureGridsManagementPage;
